import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Typography,
  LinearProgress,
  Card,
  CardContent,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
  GridToolbar
} from '@mui/x-data-grid';
import {
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Science as ResearchIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  RestartAlt as RedoIcon
} from '@mui/icons-material';
import { schemaApiService, Schema } from '../../services/schemaApiService';
import RedoSchemaDialog from './RedoSchemaDialog';

interface SchemaListProps {
  onSchemaSelect: (schema: Schema) => void;
}

const SchemaList: React.FC<SchemaListProps> = ({ onSchemaSelect }) => {
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [loading, setLoading] = useState(true);
  const [redoDialogOpen, setRedoDialogOpen] = useState(false);
  const [selectedSchemaForRedo, setSelectedSchemaForRedo] = useState<Schema | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSchemas();
  }, [page, pageSize, search, statusFilter]);

  const fetchSchemas = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await schemaApiService.listSchemas({
        limit: pageSize,
        offset: page * pageSize,
        search: search || undefined,
        status: statusFilter
      });
      
      setSchemas(response.data);
      setTotal(response.total);
    } catch (error) {
      console.error('Error fetching schemas:', error);
      setError('Failed to load schemas. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (model: any) => {
    setPage(model.page);
    setPageSize(model.pageSize);
  };

  const handleCopySchema = async (schema: Schema) => {
    const success = await schemaApiService.copySchemaToClipboard(schema);
    if (success) {
      setCopiedId(schema.id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleResearch = async (schema: Schema) => {
    try {
      await schemaApiService.triggerResearch(schema.id);
      // Refresh the list
      fetchSchemas();
    } catch (error) {
      console.error('Error triggering research:', error);
    }
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'approved': return 'primary';
      case 'deployed': return 'success';
      case 'archived': return 'error';
      case 'skipped': return 'warning';
      default: return 'default';
    }
  };

  const getCompletenessIcon = (score: number | null) => {
    if (!score) return <ErrorIcon color="error" />;
    if (score >= 90) return <CheckIcon color="success" />;
    if (score >= 70) return <WarningIcon color="warning" />;
    return <ErrorIcon color="error" />;
  };

  // Define columns for DataGrid
  const columns: GridColDef[] = [
    {
      field: 'url',
      headerName: 'URL',
      flex: 1,
      minWidth: 350,
      renderCell: (params: GridRenderCellParams) => {
        // Fix URL format if it's mangled
        let url = params.value as string;
        if (!url.startsWith('http')) {
          // Convert www_newyork_co_uk_... back to proper URL
          url = url.replace(/^www_/, 'https://www.')
                   .replace(/_co_uk_/, '.co.uk/')
                   .replace(/_/g, '-')
                   .replace(/-$/, '/');
        }
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinkIcon sx={{ fontSize: 16, color: 'action.disabled' }} />
            <Tooltip title={url}>
              <Typography
                variant="body2"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
                onClick={() => window.open(url, '_blank')}
              >
                {url.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
              </Typography>
            </Tooltip>
          </Box>
        );
      }
    },
    {
      field: 'completeness_score',
      headerName: 'Score',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getCompletenessIcon(params.value as number)}
          <Typography variant="body2" fontWeight={params.value >= 90 ? 'bold' : 'normal'}>
            {params.value || 0}%
          </Typography>
        </Box>
      )
    },
    {
      field: 'content_type',
      headerName: 'Type',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.row.is_skipped ? (
            <Tooltip title={params.row.reason}>
              <Chip
                label="Skipped"
                size="small"
                color="default"
                variant="filled"
                sx={{ borderRadius: 1 }}
              />
            </Tooltip>
          ) : (
            <Chip
              label={params.value || 'Unknown'}
              size="small"
              variant="outlined"
              sx={{ borderRadius: 1 }}
            />
          )}
          {params.row.content_type === 'free-attraction' && 
           params.row.url?.includes('madison-square-garden') && (
            <Tooltip title="Madison Square Garden might be better as a 'landmark'">
              <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
            </Tooltip>
          )}
        </Box>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={getStatusColor(params.value as string)}
          variant="filled"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
          {params.row.is_skipped ? (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {params.row.detected_type || 'N/A'}
              </Typography>
              <Tooltip title="Generate Schema with Selected Type">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedSchemaForRedo(params.row as Schema);
                    setRedoDialogOpen(true);
                  }}
                  sx={{ color: 'primary.main' }}
                >
                  <RedoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <>
              <Tooltip title="View Details">
                <IconButton
                  size="small"
                  onClick={() => onSchemaSelect(params.row as Schema)}
                  sx={{ color: 'primary.main' }}
                >
                  <ViewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={copiedId === params.row.id ? "Copied!" : "Copy Schema"}>
                <IconButton
                  size="small"
                  onClick={() => handleCopySchema(params.row as Schema)}
                  sx={{ color: copiedId === params.row.id ? 'success.main' : 'action.active' }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Research Missing Data">
                <IconButton
                  size="small"
                  onClick={() => handleResearch(params.row as Schema)}
                  disabled={params.row.completeness_score === 100}
                  sx={{ color: 'secondary.main' }}
                >
                  <ResearchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Regenerate with Different Type">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedSchemaForRedo(params.row as Schema);
                    setRedoDialogOpen(true);
                  }}
                  sx={{ color: 'warning.main' }}
                >
                  <RedoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header Section */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Schema Library
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Total Schemas: <strong>{total}</strong>
            </Typography>
            {loading && <CircularProgress size={20} />}
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search URLs (e.g., newyork.co.uk)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flexGrow: 1, minWidth: 300, maxWidth: 600 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant={statusFilter === undefined ? 'contained' : 'outlined'}
              onClick={() => setStatusFilter(undefined)}
              size="small"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 60
              }}
            >
              All
            </Button>
            <Button
              variant={statusFilter === 'draft' ? 'contained' : 'outlined'}
              onClick={() => setStatusFilter('draft')}
              size="small"
              color="info"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Draft
            </Button>
            <Button
              variant={statusFilter === 'approved' ? 'contained' : 'outlined'}
              onClick={() => setStatusFilter('approved')}
              size="small"
              color="primary"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Approved
            </Button>
            <Button
              variant={statusFilter === 'deployed' ? 'contained' : 'outlined'}
              onClick={() => setStatusFilter('deployed')}
              size="small"
              color="success"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none'
              }}
            >
              Deployed
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {/* Empty State */}
      {!loading && schemas.length === 0 && (
        <Card sx={{ textAlign: 'center', py: 8, backgroundColor: 'background.default' }}>
          <CardContent>
            <Box sx={{ mb: 3 }}>
              <ErrorIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No schemas found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {search ? `No schemas matching "${search}"` : 'No schemas have been generated yet for newyork.co.uk'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              To get started, click "Create Tables" then "Seed Data" in the status panel above
            </Typography>
            {search && (
              <Button
                variant="outlined"
                onClick={() => setSearch('')}
                sx={{ mt: 2 }}
              >
                Clear Search
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Data Grid */}
      {(schemas.length > 0 || loading) && (
        <Paper sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <DataGrid
            rows={schemas}
            columns={columns}
            rowCount={total}
            loading={loading}
            pageSizeOptions={[10, 25, 50, 100]}
            paginationModel={{ page, pageSize }}
            paginationMode="server"
            onPaginationModelChange={handlePaginationChange}
            disableRowSelectionOnClick
            autoHeight={false}
            sx={{
              border: 'none',
              '& .MuiDataGrid-root': {
                border: 'none',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                py: 1.5,
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'background.default',
                borderBottom: '2px solid',
                borderColor: 'divider',
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
              '& .MuiDataGrid-virtualScroller': {
                minHeight: 400,
              },
              '& .MuiDataGrid-footerContainer': {
                borderTop: '2px solid',
                borderColor: 'divider',
                backgroundColor: 'background.default',
              },
            }}
            components={{
              LoadingOverlay: () => (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%'
                }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 2 }}>
                    Loading schemas...
                  </Typography>
                </Box>
              ),
            }}
          />
        </Paper>
      )}
      
      {/* Redo Schema Dialog */}
      <RedoSchemaDialog
        open={redoDialogOpen}
        onClose={() => {
          setRedoDialogOpen(false);
          setSelectedSchemaForRedo(null);
        }}
        schema={selectedSchemaForRedo}
        onSuccess={() => {
          fetchSchemas(); // Refresh the list
        }}
      />
    </Box>
  );
};

export default SchemaList;